import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search as SearchIcon, X, Loader2, Sparkles, Globe } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import * as api from '../lib/api';
import { RecommendationCard } from '../components/RecommendationCard';
import { CategoryChips } from '../components/CategoryChips';
import { PersonPicker } from '../components/PersonPicker';
import { usePeople } from '../lib/usePeople';
import { greetingFor } from '../lib/format';

export const Feed = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [selectedPeople, setSelectedPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const people = usePeople(user, isAuthenticated && !authLoading);

  const peopleKey = selectedPeople.join(',');
  const filtering = Boolean(query.trim() || category || selectedPeople.length);

  // Filtered views go through the connection-scoped, regex-safe backend search.
  // Narrowing by person is enforced server-side against your visible authors.
  const load = useCallback(async (q, cat, who) => {
    try {
      setLoading(true);
      const data = (q.trim() || cat || who.length)
        ? await api.searchRecommendations(q.trim(), cat, who)
        : await api.getFeed();
      setItems(data);
      setError(null);
    } catch (err) {
      setError('We could not load recommendations just now.');
    } finally {
      setLoading(false);
    }
  }, []);

  // One effect drives every fetch: mount, auth change, search typing (debounced)
  // and category change. The cleanup cancels superseded requests.
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setItems([]);
      setLoading(false);
      return;
    }
    const t = setTimeout(() => load(query, category, selectedPeople), query ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, query, category, peopleKey]);

  const handleSaveChange = (recId, isSaved) => {
    setItems((prev) => prev.map((r) => (r.id === recId ? { ...r, isSaved } : r)));
  };

  const handleDeleted = (recId) => {
    setItems((prev) => prev.filter((r) => r.id !== recId));
  };

  const clearFilters = () => {
    setQuery('');
    setCategory('');
    setSelectedPeople([]);
  };

  if (authLoading) {
    return (
      <div className="page" data-testid="feed-page">
        <div className="loading-state"><Loader2 className="loading-spinner" /></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="page" data-testid="feed-page">
        <header className="feed-header">
          <span className="feed-brand">irecommend</span>
          <h1 className="feed-greeting">Recommendations from people you trust</h1>
          <p className="feed-sub">Sign in to see recommendations from your connections.</p>
        </header>
        <div className="placeholder">
          <button
            className="profile-signin-btn"
            onClick={() => navigate('/signin')}
            data-testid="landing-signin"
          >
            Sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page" data-testid="feed-page">
      <header className="feed-header">
        <span className="feed-brand">irecommend</span>
        <h1 className="feed-greeting" data-testid="feed-greeting">{greetingFor(user?.name)}</h1>
        <p className="feed-sub">Recommendations from people you trust.</p>
        <button
          className="explore-entry"
          onClick={() => navigate('/explore')}
          data-testid="feed-explore-entry"
        >
          <Globe className="explore-entry-icon" />
          Explore the wider network
        </button>
      </header>

      <div className="filter-bar">
        <div className="search-field">
          <SearchIcon className="search-field-icon" />
          <input
            type="text"
            className="search-field-input"
            placeholder="Search recommendations…"
            aria-label="Search recommendations"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="feed-search"
          />
          {query && (
            <button
              className="search-field-clear"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              data-testid="feed-search-clear"
            >
              <X size={16} />
            </button>
          )}
        </div>
        <CategoryChips value={category} onChange={setCategory} idPrefix="feed" />
        <PersonPicker people={people} value={selectedPeople} onChange={setSelectedPeople} idPrefix="feed" allowSelfOnly />
      </div>

      {loading ? (
        <div className="loading-state"><Loader2 className="loading-spinner" /></div>
      ) : error ? (
        <div className="placeholder" data-testid="feed-error">
          <p className="placeholder-text">{error}</p>
          <button className="placeholder-btn" onClick={() => load(query, category, selectedPeople)}>Try again</button>
        </div>
      ) : items.length > 0 ? (
        <div className="feed-list" data-testid="feed-list">
          {items.map((rec) => (
            <RecommendationCard
              key={rec.id}
              recommendation={rec}
              onSaveChange={handleSaveChange}
              onDeleted={handleDeleted}
            />
          ))}
        </div>
      ) : filtering ? (
        <div className="placeholder" data-testid="feed-no-results">
          <SearchIcon className="placeholder-icon" />
          <h2 className="placeholder-title">No recommendations found</h2>
          <p className="placeholder-text">
            Try a different word, another category, or all people.
          </p>
          <button className="placeholder-btn" onClick={clearFilters} data-testid="feed-clear-filters">
            Clear filters
          </button>
        </div>
      ) : (
        <div className="placeholder" data-testid="feed-empty">
          <Sparkles className="placeholder-icon" />
          <h2 className="placeholder-title">Your trusted recommendations start here</h2>
          <p className="placeholder-text">
            Invite people whose recommendations you trust. When they recommend something,
            you&apos;ll see it here.
          </p>
          <div className="placeholder-actions">
            <button
              className="placeholder-btn"
              onClick={() => navigate('/profile')}
              data-testid="feed-empty-invite"
            >
              Invite someone
            </button>
            <button
              className="placeholder-btn secondary"
              onClick={() => navigate('/add')}
              data-testid="feed-empty-recommend"
            >
              Make your first recommendation
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
